import { useState } from "react";
import { Session, makeRequest } from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import PrimaryButton from "../components/PrimaryButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import Warning from "../components/Warning";
import { formatDisplayAmount } from "../lib/formatDisplayAmount";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { refundChainOf } from "../lib/refundChain";

type Props = {
  session: Session;
  refundToken?: string;
  onBack: () => void;
  onConfirm: (session: Session) => void;
};

export default function ConfirmRefund({
  session,
  refundToken,
  onBack,
  onConfirm,
}: Props) {
  const { glideConfig } = useGlideDepositConfig();
  const [isCanceling, setIsCanceling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const paidStr = session.actualPaymentAmount || "0";
  const currencySymbol =
    session.actualPaymentCurrencySymbol || session.paymentCurrencySymbol || "";
  const refundChain = refundChainOf(session);

  // The transfer keeps waiting for a top-up until it is explicitly canceled, so
  // cancel before showing the refund form. Otherwise a payer who closes the
  // modal here leaves their funds waiting on the deposit address, and the
  // refund can only be issued once the transfer reaches a refundable state.
  const onCancel = async () => {
    setIsCanceling(true);
    setErrorMessage(null);
    try {
      await makeRequest(glideConfig, {
        url: `/sessions/${session.sessionId}/cancel`,
        method: "POST",
        body: { refundToken },
      });
      onConfirm(session);
    } catch (error) {
      logger.error(`[ConfirmRefund] Failed to cancel transaction: ${error}`);
      setErrorMessage("Failed to cancel the transaction. Please try again.");
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <>
      <ModalBackButton onClick={onBack} />

      <TransactionCheckoutHeader title="Cancel transaction?" />

      <p className="text-sm text-[--color-text-secondary] text-center mb-4">
        This will cancel your transaction and refund{" "}
        <span className="font-medium text-[--color-text-primary]">
          {formatDisplayAmount(paidStr)} {currencySymbol}
        </span>
        {refundChain.isConverted &&
          ` as ${refundChain.currencySymbol} on ${refundChain.name}`}{" "}
        to your wallet. You will be able to provide your refund wallet address
        in the next step.
      </p>

      <PrimaryButton onClick={() => onCancel()} loading={isCanceling}>
        Cancel Transaction
      </PrimaryButton>

      {errorMessage && (
        <Warning className="mt-4">
          <div className="font-medium mb-1">Error</div>
          <div>{errorMessage}</div>
        </Warning>
      )}

      <button
        className="w-full mt-2 py-2 text-sm text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
        onClick={onBack}
      >
        Go Back
      </button>
    </>
  );
}
